import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { celebrateDailyTasks } from "../utils/daily_celebration"
import { showSystemToast } from "../utils/system_toast"
import { hostTaskPane, taskPaneTrigger } from "../utils/task_pane"

// Galaxy view for a task list (tasks/_galaxy). The 3D scene is its own bundle,
// tasks_galaxy.js, which publishes mount() on window.JaneTasksGalaxy - so this
// controller, which ships in application.js, never pulls three.js into every
// page. It waits for the bundle, mounts the scene, and owns the HTML chrome:
// the hover tooltip, the card for a selected task, and completing it. Complete
// goes through the same /tasks/:id/complete.json the board uses, and the scene
// only flares the body once the server agrees.
const PRIORITY_NAMES = { 1: "Urgent", 2: "High", 4: "Low" }

export default class extends Controller {
    static targets = ["stage", "loading", "unsupported", "tooltip", "card", "cardTitle", "cardMeta", "cardComplete", "cardCompleteLabel", "cardOpen", "pause"]
    static values = { bodies: Array, title: String, list: String, interactive: Boolean }

    connect() {
        this._byId = new Map(this.bodiesValue.map((b) => [String(b.id), b]))
        this._selected = null
        this._paused = false
        this._onReady = () => this._mount()
        this._onKey = (e) => {
            if (e.key === "Escape" && this._selected) this.deselect()
        }
        document.addEventListener("keydown", this._onKey)
        if (window.JaneTasksGalaxy) this._mount()
        else window.addEventListener("jane-tasks-galaxy:ready", this._onReady, { once: true })
        // The task pane opens in this view's sidebar (detail-modal on the same
        // element); a task it completes or reopens moves to or from the dust.
        this._unhostPane = hostTaskPane(this, { onCompleted: (id, done) => this._applyDone(id, done) })
    }

    disconnect() {
        this._unhostPane?.()
        window.removeEventListener("jane-tasks-galaxy:ready", this._onReady)
        document.removeEventListener("keydown", this._onKey)
        this._scene?.destroy()
        this._scene = null
    }

    _mount() {
        if (this._scene || !this.element.isConnected) return
        const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
        this._scene = window.JaneTasksGalaxy.mount(this.stageTarget, {
            bodies: this.bodiesValue,
            title: this.titleValue,
            reducedMotion,
            onHover: (body, at) => this._hover(body, at),
            onSelect: (body) => (body ? this._select(body) : this.deselect())
        })
        this.loadingTarget.hidden = true
        if (!this._scene) {
            this.unsupportedTarget.hidden = false
            this.element.dataset.tasksGalaxyState = "unsupported"
            return
        }
        // Reduced motion starts still, so the pause button says play.
        if (reducedMotion) this._setPaused(true)
        this.element.dataset.tasksGalaxyState = "ready"
    }

    _hover(body, at) {
        const tip = this.tooltipTarget
        if (!body || (this._selected && String(body.id) === String(this._selected.id))) {
            tip.hidden = true
            return
        }
        tip.replaceChildren()
        const title = document.createElement("strong")
        title.textContent = body.title
        tip.appendChild(title)
        const meta = this._meta(body)
        if (meta) {
            const line = document.createElement("span")
            line.textContent = meta
            tip.appendChild(line)
        }
        tip.hidden = false
        const box = this.element.getBoundingClientRect()
        tip.style.left = `${Math.min(at.x + 14, box.width - tip.offsetWidth - 8)}px`
        tip.style.top = `${Math.min(at.y + 14, box.height - tip.offsetHeight - 8)}px`
    }

    _meta(body) {
        const live = this._byId.get(String(body.id)) || body
        return [
            live.done ? "Done" : null,
            PRIORITY_NAMES[live.priority],
            live.due_label ? (live.due_state === "overdue" ? `Overdue - ${live.due_label}` : `Due ${live.due_label}`) : null,
            body.openBelow ? `${body.openBelow} open ${body.openBelow === 1 ? "subtask" : "subtasks"}` : null
        ].filter(Boolean).join(" · ")
    }

    _select(body) {
        this._selected = body
        this.tooltipTarget.hidden = true
        this._scene?.focus(body.id)
        this.cardTitleTarget.textContent = body.title
        this.cardMetaTarget.textContent = this._meta(body)
        this.cardMetaTarget.hidden = !this.cardMetaTarget.textContent
        this.cardOpenTarget.href = body.href
        this._paintComplete()
        this.cardTarget.hidden = false
    }

    deselect() {
        this._selected = null
        this.cardTarget.hidden = true
        this._scene?.focus(null)
    }

    reset() {
        this.deselect()
        this._scene?.reset()
    }

    // Scroll and pinch zoom too; the buttons are for a mouse with no wheel and
    // for anyone who never guesses a canvas zooms.
    zoomIn() {
        this._scene?.zoomBy(0.65)
    }

    zoomOut() {
        this._scene?.zoomBy(1.55)
    }

    togglePause() {
        this._setPaused(!this._paused)
    }

    _setPaused(paused) {
        this._paused = paused
        this._scene?.setPaused(paused)
        if (!this.hasPauseTarget) return
        const label = paused ? "Play orbits" : "Pause orbits"
        this.pauseTarget.setAttribute("aria-label", label)
        this.pauseTarget.title = label
        this.pauseTarget.querySelector("i").className = `bi ${paused ? "bi-play-fill" : "bi-pause-fill"}`
    }

    _paintComplete() {
        if (!this.hasCardCompleteTarget || !this._selected) return
        const done = Boolean(this._byId.get(String(this._selected.id))?.done)
        this.cardCompleteLabelTarget.textContent = done ? "Reopen" : "Complete"
        this.cardCompleteTarget.classList.toggle("btn-primary", !done)
        this.cardCompleteTarget.classList.toggle("btn-outline-light", done)
    }

    toggleComplete() {
        if (!this.interactiveValue || !this._selected || this._busy) return
        const body = this._byId.get(String(this._selected.id))
        if (!body) return
        const wasDone = Boolean(body.done)
        this._busy = true
        this.cardCompleteTarget.disabled = true
        this._post(`/tasks/${body.id}/${wasDone ? "uncomplete" : "complete"}.json`).then((data) => {
            this._busy = false
            this.cardCompleteTarget.disabled = false
            if (!data || !data.ok) {
                showSystemToast("Couldn't update that task", { level: "error", duration: 3000 })
                return
            }
            this._applyDone(String(body.id), !wasDone)
            if (!wasDone) celebrateDailyTasks(data.celebration)
            // A repeating task's next occurrence is a new task the server made;
            // it joins the galaxy on the next visit, so say where it went.
            if (!wasDone && data.spawned) {
                const due = data.spawned.due_date ? new Date(`${data.spawned.due_date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : null
                showSystemToast(due ? `Done - the next one is on ${due}` : "Done - the next one is on the list", { level: "success", duration: 3000 })
            }
        })
    }

    _applyDone(id, done) {
        const body = this._byId.get(id)
        if (!body) return
        body.done = done
        this._scene?.setDone(body.id, done)
        if (this._selected && String(this._selected.id) === id) {
            this.cardMetaTarget.textContent = this._meta(this._selected)
            this._paintComplete()
        }
    }

    // The card's Details button: the whole task in the sidebar.
    openPane() {
        if (!this._selected) return
        this.dispatch("open-detail", { detail: { trigger: taskPaneTrigger(this._selected.id) } })
    }

    _post(url) {
        const target = this.listValue ? `${url}?list=${encodeURIComponent(this.listValue)}` : url
        return fetch(target, {
            method: "POST",
            headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
            body: new FormData(),
            credentials: "same-origin"
        }).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    }
}
